import type {VisualEmployee} from './OrdinaryWorkforceHome';

/** Design-review fixtures only. Never feed these simulated activities or app examples to the agent runtime. */
export type ReviewApp={label:string;kind:string;purpose:string};
export type ReviewEvent={id:string;title:string;time:string;status:'In progress'|'Needs review'|'Done';description:string;summary:string;files:string[];details:string[];steps:{time:string;title:string;detail:string;complete:boolean}[]};
type ReviewScenario={title:string;desc:string;progress:number;next:string;steps:string[];items:string[];apps:string[];stem:string};
const SCENARIOS:Record<string,ReviewScenario>={
 "praamis_executive": {
  "title": "Coordinating today's priorities",
  "desc": "Balancing employee assignments, reviewing blockers, and preparing decisions for the owner.",
  "progress": 64,
  "next": "Draft owner briefing",
  "steps": [
   "Reviewed workforce goals",
   "Matched available employees",
   "Assigned high-priority work",
   "Flagged decisions for approval",
   "Prepared owner review"
  ],
  "items": [
   "Briefing prepared",
   "Priority decisions",
   "Reviewed task queue",
   "Checked approvals",
   "Routed new assignments",
   "Identified blockers",
   "Team coordination"
  ],
  "apps": [
   "SALRI",
   "Calendar",
   "Drive",
   "Browser"
  ],
  "stem": "praamis"
 },
 "workflow_operations_manager": {
  "title": "Organizing workforce handoffs",
  "desc": "Coordinating cross-agent assignments, dependency checks, and task handoffs.",
  "progress": 72,
  "next": "Review task handoffs",
  "steps": [
   "Reviewed incoming work",
   "Mapped agent responsibilities",
   "Sequenced dependencies",
   "Flagged stalled handoffs",
   "Prepared owner review"
  ],
  "items": [
   "Updated workflow board",
   "Routed tasks to team",
   "Checked dependencies",
   "Flagged blocker",
   "Reviewed handoffs",
   "Confirmed assignments",
   "Reviewed queue"
  ],
  "apps": [
   "SALRI",
   "Calendar",
   "Drive",
   "Browser"
  ],
  "stem": "workflow-operations"
 },
 "research_intelligence": {
  "title": "Researching new opportunities",
  "desc": "Comparing current opportunities and preparing a source-checked shortlist.",
  "progress": 62,
  "next": "Review opportunity shortlist",
  "steps": [
   "Collected opportunity sources",
   "Compared requirements",
   "Checked source credibility",
   "Ranked qualifying leads",
   "Prepared owner review"
  ],
  "items": [
   "Ranked opportunities",
   "Verified sources",
   "Compared requirements",
   "Prepared lead brief",
   "Checked duplicates",
   "Collected new leads",
   "Reviewed search criteria"
  ],
  "apps": [
   "Browser",
   "Drive",
   "SALRI",
   "Calendar"
  ],
  "stem": "research"
 },
 "revenue_director": {
  "title": "Planning revenue opportunities",
  "desc": "Prioritizing potential revenue channels and forecast scenarios.",
  "progress": 56,
  "next": "Review revenue plan",
  "steps": [
   "Reviewed available channels",
   "Compared earning potential",
   "Estimated implementation effort",
   "Prepared revenue scenarios",
   "Prepared owner review"
  ],
  "items": [
   "Reviewed revenue plan",
   "Estimated scenarios",
   "Ranked channels",
   "Prepared proposal",
   "Checked conversion goals",
   "Updated forecast",
   "Reviewed income sources"
  ],
  "apps": [
   "SALRI",
   "Sheets",
   "Drive",
   "Browser"
  ],
  "stem": "revenue"
 },
 "sentinel_auditor": {
  "title": "Reviewing quality checks",
  "desc": "Checking proposed work against quality and safety requirements.",
  "progress": 77,
  "next": "Review quality findings",
  "steps": [
   "Received work submissions",
   "Inspected evidence and output",
   "Checked policy requirements",
   "Flagged incomplete claims",
   "Prepared owner review"
  ],
  "items": [
   "Reviewed QC assessment",
   "Verified evidence",
   "Checked deliverables",
   "Flagged issue",
   "Reviewed requirements",
   "Audited task claim",
   "Opened review batch"
  ],
  "apps": [
   "SALRI",
   "Drive",
   "GitHub",
   "Browser"
  ],
  "stem": "sentinel-auditor"
 },
 "product_director": {
  "title": "Preparing the daily work report",
  "desc": "Compiling employee progress, verified results, and owner attention items.",
  "progress": 68,
  "next": "Review daily briefing",
  "steps": [
   "Collected team updates",
   "Grouped report sections",
   "Separated verified work from drafts",
   "Highlighted pending approvals",
   "Prepared owner review"
  ],
  "items": [
   "Prepared daily briefing",
   "Reviewed completed work",
   "Checked team updates",
   "Grouped action items",
   "Flagged approvals",
   "Updated report",
   "Collected statuses"
  ],
  "apps": [
   "SALRI",
   "Calendar",
   "Drive",
   "Browser"
  ],
  "stem": "product"
 },
 "finance_performance": {
  "title": "Reviewing budget allocation",
  "desc": "Preparing a spending and reinvestment breakdown from sample financial data.",
  "progress": 58,
  "next": "Review budget breakdown",
  "steps": [
   "Organized finance categories",
   "Reviewed proposed expenses",
   "Calculated allocation options",
   "Checked spending limits",
   "Prepared owner review"
  ],
  "items": [
   "Reviewed allocations",
   "Updated budget draft",
   "Checked limits",
   "Prepared summary",
   "Compared balances",
   "Reviewed expenses",
   "Opened finance review"
  ],
  "apps": [
   "SALRI",
   "Sheets",
   "Drive",
   "Browser"
  ],
  "stem": "finance-performance"
 },
 "sales_director": {
  "title": "Reviewing active sales leads",
  "desc": "Organizing lead outreach and preparing follow-up decisions.",
  "progress": 66,
  "next": "Review lead strategy",
  "steps": [
   "Grouped incoming leads",
   "Checked contact readiness",
   "Ranked lead priorities",
   "Drafted follow-up plans",
   "Prepared owner review"
  ],
  "items": [
   "Prepared lead summary",
   "Reviewed response plans",
   "Ranked opportunities",
   "Updated pipeline",
   "Drafted follow-ups",
   "Grouped contacts",
   "Reviewed sales queue"
  ],
  "apps": [
   "SALRI",
   "Gmail",
   "Sheets",
   "Browser"
  ],
  "stem": "sales"
 },
 "marketing_distribution": {
  "title": "Planning the next campaign",
  "desc": "Reviewing channel priorities, creative needs, and campaign milestones.",
  "progress": 73,
  "next": "Review campaign outline",
  "steps": [
   "Compared target audiences",
   "Reviewed content performance",
   "Mapped distribution channels",
   "Scheduled campaign steps",
   "Prepared owner review"
  ],
  "items": [
   "Prepared campaign plan",
   "Compared channels",
   "Reviewed creative brief",
   "Mapped audiences",
   "Updated schedule",
   "Organized assets",
   "Reviewed goals"
  ],
  "apps": [
   "YouTube",
   "Drive",
   "SALRI",
   "Browser"
  ],
  "stem": "marketing-distribution"
 },
 "brand_identity": {
  "title": "Refining the visual identity",
  "desc": "Preparing a brand direction, color rules, and asset handoff for review.",
  "progress": 70,
  "next": "Review brand concepts",
  "steps": [
   "Reviewed existing brand assets",
   "Compared style references",
   "Defined color treatments",
   "Checked consistency",
   "Prepared owner review"
  ],
  "items": [
   "Prepared concept board",
   "Reviewed logo variants",
   "Checked brand colors",
   "Organized references",
   "Updated style guide",
   "Reviewed mockups",
   "Opened design brief"
  ],
  "apps": [
   "Figma",
   "Drive",
   "Browser",
   "SALRI"
  ],
  "stem": "brand-identity"
 },
 "content_production": {
  "title": "Creating content for review",
  "desc": "Reviewing research from the research agent, selecting highlights, and preparing a short video for approval.",
  "progress": 78,
  "next": "Review approved captions",
  "steps": [
   "Received research from the research agent",
   "Analyzed the source clips",
   "Cut a short video draft",
   "Organized video assets",
   "Prepared owner review"
  ],
  "items": [
   "Working on content",
   "Submitted to review",
   "Organized files",
   "Edited video",
   "Analyzed clips",
   "Received research",
   "Team sync"
  ],
  "apps": [
   "YouTube",
   "Drive",
   "SALRI",
   "Browser"
  ],
  "stem": "content-production"
 },
 "publishing_marketplace": {
  "title": "Preparing scheduled posts",
  "desc": "Organizing approved assets and channel-specific publishing requirements.",
  "progress": 74,
  "next": "Review publishing queue",
  "steps": [
   "Received content package",
   "Checked channel formatting",
   "Validated posting schedule",
   "Prepared draft captions",
   "Prepared owner review"
  ],
  "items": [
   "Prepared publishing queue",
   "Checked posting windows",
   "Updated captions",
   "Organized assets",
   "Reviewed channel specs",
   "Received content",
   "Prepared checklist"
  ],
  "apps": [
   "YouTube",
   "Drive",
   "Calendar",
   "Browser"
  ],
  "stem": "publishing-marketplace"
 },
 "operations_fulfillment": {
  "title": "Tracking fulfillment requests",
  "desc": "Mapping deliveries, assigned owners, and outstanding operational steps.",
  "progress": 61,
  "next": "Review fulfillment queue",
  "steps": [
   "Reviewed incoming requests",
   "Checked required resources",
   "Assigned execution steps",
   "Monitored open items",
   "Prepared owner review"
  ],
  "items": [
   "Updated fulfillment queue",
   "Assigned action items",
   "Checked handoffs",
   "Reviewed requests",
   "Prepared status",
   "Organized tasks",
   "Reviewed blockers"
  ],
  "apps": [
   "SALRI",
   "Drive",
   "Sheets",
   "Calendar"
  ],
  "stem": "operations-fulfillment"
 },
 "legal_compliance": {
  "title": "Reviewing policy requirements",
  "desc": "Checking a proposed workflow against available legal and platform rules.",
  "progress": 69,
  "next": "Review compliance checklist",
  "steps": [
   "Collected applicable rules",
   "Reviewed workflow description",
   "Identified compliance questions",
   "Marked items needing counsel",
   "Prepared owner review"
  ],
  "items": [
   "Prepared compliance review",
   "Checked platform rules",
   "Flagged policy risks",
   "Reviewed disclaimers",
   "Organized references",
   "Updated checklist",
   "Opened request"
  ],
  "apps": [
   "Browser",
   "Drive",
   "Gmail",
   "Sheets"
  ],
  "stem": "legal-compliance"
 },
 "goal_achievement": {
  "title": "Updating goal progress",
  "desc": "Comparing planned milestones and preparing an action plan.",
  "progress": 65,
  "next": "Review progress milestones",
  "steps": [
   "Reviewed stated goals",
   "Mapped measurable milestones",
   "Compared planned progress",
   "Flagged missed checkpoints",
   "Prepared owner review"
  ],
  "items": [
   "Updated milestone plan",
   "Reviewed target dates",
   "Organized goals",
   "Checked progress",
   "Flagged obstacle",
   "Prepared summary",
   "Opened weekly review"
  ],
  "apps": [
   "SALRI",
   "Sheets",
   "Calendar",
   "Drive"
  ],
  "stem": "goal-achievement"
 },
 "debt_management": {
  "title": "Reviewing debt reduction options",
  "desc": "Organizing balances and illustrating alternative payoff sequences.",
  "progress": 60,
  "next": "Review payoff outline",
  "steps": [
   "Listed outstanding obligations",
   "Compared payment options",
   "Estimated payoff timelines",
   "Checked budget constraints",
   "Prepared owner review"
  ],
  "items": [
   "Drafted payoff outline",
   "Reviewed obligations",
   "Checked payment order",
   "Estimated timeline",
   "Organized notes",
   "Updated debt overview",
   "Reviewed budget"
  ],
  "apps": [
   "SALRI",
   "Sheets",
   "Drive",
   "Calendar"
  ],
  "stem": "debt-management"
 },
 "engineering_director": {
  "title": "Reviewing system reliability",
  "desc": "Analyzing implementation tasks and preparing a controlled test plan.",
  "progress": 80,
  "next": "Review deployment checklist",
  "steps": [
   "Checked recent code changes",
   "Reviewed integration dependencies",
   "Identified test coverage gaps",
   "Verified staging requirements",
   "Prepared owner review"
  ],
  "items": [
   "Prepared test checklist",
   "Reviewed pull requests",
   "Checked dependencies",
   "Analyzed logs",
   "Organized fixes",
   "Reviewed build",
   "Opened engineering queue"
  ],
  "apps": [
   "GitHub",
   "Cloudflare",
   "Render",
   "Supabase"
  ],
  "stem": "engineering"
 },
 "personal_life_assistant": {
  "title": "Organizing upcoming commitments",
  "desc": "Preparing a consolidated calendar and personal task checklist.",
  "progress": 71,
  "next": "Review weekly schedule",
  "steps": [
   "Collected proposed reminders",
   "Compared calendar availability",
   "Prioritized personal errands",
   "Grouped due items",
   "Prepared owner review"
  ],
  "items": [
   "Prepared weekly schedule",
   "Organized reminders",
   "Checked appointments",
   "Updated errands",
   "Prepared checklist",
   "Reviewed commitments",
   "Opened planning session"
  ],
  "apps": [
   "Calendar",
   "Gmail",
   "Drive",
   "Browser"
  ],
  "stem": "personal-life-assistant"
 },
 "income_builder": {
  "title": "Finding zero-cost income options",
  "desc": "Screening potential tasks that do not require spending or purchases.",
  "progress": 67,
  "next": "Review task shortlist",
  "steps": [
   "Searched available task sources",
   "Removed paid-entry opportunities",
   "Compared time requirements",
   "Flagged verification gaps",
   "Prepared owner review"
  ],
  "items": [
   "Prepared income shortlist",
   "Screened listings",
   "Checked requirements",
   "Removed paid options",
   "Ranked tasks",
   "Verified sources",
   "Reviewed criteria"
  ],
  "apps": [
   "Browser",
   "SALRI",
   "Sheets",
   "Drive"
  ],
  "stem": "income"
 },
 "markets_intelligence": {
  "title": "Preparing market intelligence",
  "desc": "Reviewing public market movements and assembling research scenarios.",
  "progress": 63,
  "next": "Review market watchlist",
  "steps": [
   "Collected available public reports",
   "Compared market indicators",
   "Cross-checked data sources",
   "Flagged changing conditions",
   "Prepared owner review"
  ],
  "items": [
   "Updated watchlist",
   "Reviewed data sources",
   "Compared trends",
   "Prepared market note",
   "Flagged changes",
   "Checked sources",
   "Opened market briefing"
  ],
  "apps": [
   "Browser",
   "Sheets",
   "Drive",
   "SALRI"
  ],
  "stem": "markets"
 },
 "runtime_safety_officer": {
  "title": "Reviewing runtime safety",
  "desc": "Checking the planned retry limits and task-stop safeguards.",
  "progress": 84,
  "next": "Review safeguard checklist",
  "steps": [
   "Reviewed automation policies",
   "Mapped retry thresholds",
   "Checked stop conditions",
   "Identified alert paths",
   "Prepared owner review"
  ],
  "items": [
   "Prepared safeguard review",
   "Checked retry rules",
   "Reviewed limits",
   "Identified alert owners",
   "Mapped stop conditions",
   "Audited task policy",
   "Reviewed status"
  ],
  "apps": [
   "Supabase",
   "Render",
   "Cloudflare",
   "GitHub"
  ],
  "stem": "runtime-safety"
 },
 "project_manager": {
  "title": "Planning project milestones",
  "desc": "Aligning delivery dates, owners, and outstanding project dependencies.",
  "progress": 76,
  "next": "Review project milestones",
  "steps": [
   "Collected open deliverables",
   "Sequenced planned work",
   "Assigned milestone owners",
   "Flagged timeline risks",
   "Prepared owner review"
  ],
  "items": [
   "Updated milestone board",
   "Reviewed deliverables",
   "Assigned owners",
   "Checked dependencies",
   "Prepared update",
   "Reviewed blockers",
   "Opened project planning"
  ],
  "apps": [
   "Calendar",
   "Drive",
   "GitHub",
   "SALRI"
  ],
  "stem": "project"
 }
};
const ICONS:Record<string,string>={'SALRI':'salri','YouTube':'youtube','Drive':'drive','Browser':'browser','Sheets':'sheet','Calendar':'calendar','GitHub':'github','Cloudflare':'cloudflare','Supabase':'supabase','Render':'render','Gmail':'gmail','Figma':'figma'};
const PURPOSES:Record<string,string>={'SALRI':'Work','YouTube':'Video','Drive':'Files','Browser':'Research','Sheets':'Data','Calendar':'Schedule','GitHub':'Code','Cloudflare':'Deploy','Supabase':'Database','Render':'Services','Gmail':'Mail','Figma':'Design'};
export function getReviewFixture(employee:VisualEmployee){
 const source=SCENARIOS[employee.roleKey]||{title:'Preparing work plan',desc:'Organizing a draft assignment for owner review.',progress:55,next:'Review draft plan',steps:['Reviewed assignment','Checked requirements','Planned steps','Organized notes','Prepared a draft'],items:['Prepared work plan','Reviewed requirements','Organized notes','Mapped next steps','Prepared checklist','Checked dependencies','Received request'],apps:['SALRI','Drive','Calendar','Browser'],stem:'work-plan'};
 const person=employee.nickname||employee.name||employee.role;
 const events:ReviewEvent[]=source.items.map((title,index)=>({
  id:employee.id+'-example-'+index,title,time:['11:02 AM','10:15 AM','9:48 AM','9:12 AM','8:20 AM','7:55 AM','7:18 AM'][index]||'7:00 AM',
  status:index===0?'In progress':index===1?'Needs review':'Done',
  description:index===0?source.desc:'Design example of '+title.toLowerCase()+' in '+person+'\'s workflow. This does not represent an actual completed task.',
  summary:index===0?source.desc+' The card illustrates the expected task details, work handoffs, and approval status.':'Example workflow history for '+title.toLowerCase()+'. Actual activities will be supplied by the authorized task event log.',
  files:index===0?[source.stem+'-preview.pdf',source.stem+'-notes.txt',source.stem+'-checklist.csv']:index===1?[source.stem+'-review.pdf']:[source.stem+'-draft.txt'],
  details:['Owner: '+person,'Area: '+employee.role,'Record type: Design example','Execution: Not connected','Approval: Not verified'],
  steps:(index===0?source.steps:[title,'Reviewed relevant information','Prepared example outcome']).map((step,i,all)=>({time:['10:44','10:46','10:52','10:58','11:02'][i]||'11:02',title:step,detail:index===0?step+' as part of the '+source.title.toLowerCase()+' example.':'Illustrative milestone for this activity.',complete:!(index===0&&i===all.length-1)}))
 }));
 return {assignment:source.title,description:source.desc,progress:source.progress,upcoming:source.next,events,apps:source.apps.map(label=>({label,kind:ICONS[label]||'other',purpose:PURPOSES[label]||'Tool'})) as ReviewApp[]};
}
